import React, { useState, useRef, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import API from "../api";
import { capture } from "../instrument";

const GroupIdLookup = ({ onChange, initialValue = "", placeholder = "Identifiant du groupe", currentId = null }) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionItemsRef = useRef([]);

  // Initialize input from initialValue
  useEffect(() => {
    if (initialValue) setSelectedGroup(initialValue);
  }, [initialValue, currentId]);

  // Auto-scroll to keep selected item in view
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionItemsRef.current[selectedIndex]) {
      suggestionItemsRef.current[selectedIndex].scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Reset refs array when suggestions change
  useEffect(() => {
    suggestionItemsRef.current = suggestionItemsRef.current.slice(0, groupSuggestions.length);
  }, [groupSuggestions]);

  const fetchGroupSuggestions = async (query) => {
    if (!query || query.length < 2) return setGroupSuggestions([]);

    try {
      setIsLoading(true);
      // Récupérer tous les groupes existants
      const response = await API.get(`/guideline`);

      if (!response.ok) return toast.error(response.message);

      // Extraire les groupIds uniques et non-null
      const uniqueGroups = [...new Set(response.data.map((item) => item.groupId).filter((groupId) => groupId && groupId.toLowerCase().includes(query.toLowerCase())))];

      setGroupSuggestions(uniqueGroups);
      setShowSuggestions(true);

      if (uniqueGroups.length > 0) setSelectedIndex(0);
      else setSelectedIndex(-1);
    } catch (error) {
      capture(error);
      console.log(error);
      toast.error("Une erreur est survenue");
    } finally {
      setIsLoading(false);
    }
  };

  // Add debounce for better performance
  useEffect(() => {
    if (inputValue?.length < 2) return;

    const debounceTimer = setTimeout(() => fetchGroupSuggestions(inputValue), 200);

    return () => clearTimeout(debounceTimer);
  }, [inputValue]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
  };

  const selectGroup = (group) => {
    setSelectedGroup(group);
    setInputValue("");
    if (onChange) onChange(group);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    // Focus back on the input with a small delay
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  };

  const removeSelectedGroup = () => {
    setSelectedGroup("");
    if (onChange) onChange("");

    // Focus back on the input with a small delay
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 50);
  };

  const navOpts = { enabled: showSuggestions && groupSuggestions.length > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => setSelectedIndex((i) => Math.min(i + 1, groupSuggestions.length - 1)), navOpts, [showSuggestions, groupSuggestions]);
  useHotkeys("up", () => setSelectedIndex((i) => Math.max(i - 1, 0)), navOpts, [showSuggestions, groupSuggestions]);
  useHotkeys("enter", () => selectGroup(groupSuggestions[selectedIndex >= 0 ? selectedIndex : 0]), navOpts, [showSuggestions, groupSuggestions, selectedIndex]);
  useHotkeys("escape", () => { setShowSuggestions(false); setSelectedIndex(-1); }, navOpts, [showSuggestions, groupSuggestions]);

  const handleKeyDown = (e) => {
    if (showSuggestions && groupSuggestions.length > 0) return;
    if (e.key === "Backspace" && inputValue === "" && selectedGroup) {
      removeSelectedGroup();
    } else if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      selectGroup(inputValue.trim());
    }
  };

  const handleContainerClick = () => {
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="space-y-2">
      <div className="relative w-full">
        <div
          className="bg-white flex flex-wrap items-center gap-1 p-2 border rounded-lg focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 transition-all duration-200 min-h-[42px] cursor-text"
          onClick={handleContainerClick}
        >
          {selectedGroup ? (
            <div className="flex items-center justify-between w-full px-1">
              <div className="flex flex-col cursor-text">
                <span className="font-medium">{selectedGroup}</span>
              </div>
              <button
                className="ml-1 text-primary-600 hover:text-primary-800 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSelectedGroup();
                }}
                type="button"
                aria-label={`Supprimer ${selectedGroup}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center flex-grow">
              <input
                ref={inputRef}
                className="flex-grow outline-none"
                placeholder={placeholder}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={() =>
                  setTimeout(() => {
                    setShowSuggestions(false);
                    setSelectedIndex(-1);
                  }, 200)
                }
                onFocus={() => {
                  if (inputValue.length >= 2) {
                    fetchGroupSuggestions(inputValue);
                  }
                }}
              />
              {isLoading && <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full mr-2"></div>}
            </div>
          )}
        </div>

        {showSuggestions && groupSuggestions.length > 0 && (
          <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-auto max-h-48">
            {groupSuggestions.map((group, idx) => (
              <div
                ref={(el) => (suggestionItemsRef.current[idx] = el)}
                key={idx}
                className={`p-3 cursor-pointer flex justify-between items-center border-b border-gray-100 last:border-b-0 ${
                  selectedIndex === idx ? "bg-primary-50 text-primary-700" : "hover:bg-gray-50"
                }`}
                onClick={() => selectGroup(group)}
              >
                <div className="flex items-center">
                  <span className="font-medium">{group}</span>
                </div>
                {selectedIndex === idx && <div className="text-primary-500 bg-primary-100 rounded px-2 py-1 text-xs font-medium">Enter ↵</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupIdLookup;
